/**
 * ComposedTokenDisplay.js — Token Counter UI
 *
 * Renders token counts in different visual styles (dots, number, die).
 * Reads current value from character.cardResources and resolves dynamic
 * max via $ref expressions.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #176)
 */

import React from 'react';
import { resolveExpression } from './expressions';
import { TokenCounter } from '../components/domain-cards/ui/ActiveStateIndicator';

/**
 * @param {object} props
 * @param {object} props.tokenDef - Token definition from mechanic.tokens
 * @param {object} props.character - Character object
 * @param {string} props.cardKey - Key for character.cardResources lookup
 * @param {string} [props.style='dots'] - Display style: 'dots' | 'number' | 'die'
 */
const ComposedTokenDisplay = ({ tokenDef, character, cardKey, style = 'dots' }) => {
  const { id: tokenId, name, max: maxExpr, icon } = tokenDef;

  // Read current value from character.cardResources
  const current = character?.cardResources?.[cardKey]?.[tokenId]?.current ?? 0;

  // Resolve max (may be $ref expression)
  const max = resolveExpression(maxExpr, { character, cardKey }) || 0;

  if (style === 'number') {
    return (
      <TokenCounter
        current={current}
        maximum={max}
        label={name}
        variant="purple"
      />
    );
  }

  if (style === 'die') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-purple-900/20 border border-purple-700 text-purple-300">
        {icon && <span>{icon}</span>}
        <span>{name}:</span>
        <span className="font-bold">🎲 {current}/{max}</span>
      </div>
    );
  }

  // Default: dots
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-purple-900/20 border border-purple-700 text-purple-300">
      {icon && <span>{icon}</span>}
      <span className="mr-1">{name}:</span>
      {Array.from({ length: max }, (_, i) => (
        <span
          key={i}
          className={`w-2.5 h-2.5 rounded-full border ${
            i < current
              ? 'bg-purple-500 border-purple-400'
              : 'bg-gray-700 border-gray-600'
          }`}
        />
      ))}
    </div>
  );
};

export default ComposedTokenDisplay;
